import { useContext, useEffect, useRef, useState } from 'react';
import { Button, Dropdown, Flex, Tag, notification } from 'antd';
import { useTranslation } from 'react-i18next';
import { orderPackOrders } from '@/apis/inventoryv2';
import { packShipments } from '@/apis/packing';
import { Shipment } from '@/types/shipment';
import AddOrderEdit from '@/pages/Shipment/pages/ShipmentListPage/components/AddOrderEdit';
import { DictContext } from '@/layout/context/DictContext';

interface ViewShipmentOrderButtonProps {
  orderId: string;
}

const ViewShipmentOrderButton = ({ orderId }: ViewShipmentOrderButtonProps) => {
  const { t } = useTranslation();
  const { shipStatus } = useContext(DictContext);
  const [loading, setLoading] = useState(false);
  const [shipments, setShipments] = useState<Shipment[] | null>(null);
  const [selectedShipment, setSelectedShipment] = useState<Shipment | null>(null);
  const requestRef = useRef<AbortController | null>(null);
  // 以发货单 ID 缓存关联结果，保留单号和状态供下拉菜单展示。
  const shipmentCacheRef = useRef<Map<string, Shipment> | null>(null);

  useEffect(() => {
    shipmentCacheRef.current = null;
    setLoading(false);
    setShipments(null);
    setSelectedShipment(null);
    return () => requestRef.current?.abort();
  }, [orderId]);

  const showShipments = (orders: Shipment[]) => {
    if (orders.length === 1) {
      setSelectedShipment(orders[0]);
    } else {
      setShipments(orders);
    }
  };

  const handleView = async () => {
    if (!orderId || loading) return;
    if (shipmentCacheRef.current !== null) {
      showShipments(Array.from(shipmentCacheRef.current.values()));
      return;
    }
    requestRef.current?.abort();
    const controller = new AbortController();
    requestRef.current = controller;
    setLoading(true);
    try {
      const packOrders = await orderPackOrders([orderId], controller);
      if (controller.signal.aborted) return;
      const packOrderIds = Array.from(new Set(packOrders.map(order => order.id)));
      const orders = packOrderIds.length > 0
        ? await packShipments(packOrderIds, controller)
        : [];
      if (controller.signal.aborted) return;
      shipmentCacheRef.current = new Map(orders.map(order => [order.id, order]));
      showShipments(Array.from(shipmentCacheRef.current.values()));
    } catch (error) {
      if (!controller.signal.aborted) {
        notification.error({ message: t('common.searchError') });
      }
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  };

  return (
    <>
      <Dropdown
        trigger={['click']}
        placement="bottomLeft"
        open={shipments !== null}
        onOpenChange={open => {
          if (open) handleView();
          else setShipments(null);
        }}
        menu={{
          items: shipments?.length ? shipments.map(shipment => {
            const status = shipStatus.find(item => item.key === shipment.status);
            return {
              key: shipment.id,
              label: (
                <Flex gap={8} align="center">
                  <span>{shipment.number}</span>
                  {status && <Tag>{status.desc}</Tag>}
                </Flex>
              ),
            };
          }) : [{ key: 'empty', disabled: true, label: t('warehouse.noData') }],
          onClick: ({ key }) => {
            const shipment = shipments?.find(item => item.id === key);
            if (shipment) setSelectedShipment(shipment);
            setShipments(null);
          },
        }}
      >
        <Button loading={loading}>{t('shipment.viewShipmentOrder')}</Button>
      </Dropdown>
      {selectedShipment && (
        <AddOrderEdit
          key={selectedShipment.id}
          formType="EDIT"
          open
          readonly
          shipment={selectedShipment}
          packingOrderId={undefined}
          shipBoxes={null}
          saleItems={null}
          purLotMap={null}
          onClose={() => setSelectedShipment(null)}
          onSuccess={() => setSelectedShipment(null)}
        />
      )}
    </>
  );
};

export default ViewShipmentOrderButton;
